import {
	createContext,
	type FC,
	type HTMLAttributes,
	type HtmlHTMLAttributes,
	type PropsWithChildren,
	type ReactNode,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import type { StateSetter } from "../helpers/types";
import "./Tabs.css";
import { useMap } from "usehooks-ts";
import { Button } from "./Button";
import { ContentLoading } from "./ContentLoading";
import { GenericErrorBoundary } from "./GenericError";

type TabSettings = {
	key: string;
	title?: ReactNode;
	disabled?: boolean;
};

type MapOrEntries<K, V> = Map<K, V> | [K, V][];

export type TabMap = Omit<Map<string, TabSettings>, "set" | "clear" | "delete">;
export type TabActions<K, V> = {
	set: (key: K, value: V) => void;
	setAll: (entries: MapOrEntries<K, V>) => void;
	remove: (key: K) => void;
	reset: Map<K, V>["clear"];
};

type TabsContextType = {
	activeTab: string | null;
	setActiveTab: StateSetter<string | null>;
	tabs: TabMap;
	tabsActions: TabActions<string, TabSettings>;
	handleTabChange: (tab: string) => void;
};

const TabsContext = createContext<TabsContextType | null>(null);

interface TabsProviderProps extends PropsWithChildren {
	onTabChange?: (tab: string) => void;
	defaultActiveKey?: string;
}

const TabsProvider: FC<Readonly<TabsProviderProps>> = ({
	children,
	defaultActiveKey,
	onTabChange,
}) => {
	const [tabs, tabsActions] = useMap<string, TabSettings>();
	const [activeTab, setActiveTab] = useState<string | null>(null);
	const defaultSet = useRef(false);

	const handleTabChange = useCallback(
		(tab: string) => {
			setActiveTab(tab);

			if (onTabChange) {
				onTabChange(tab);
			}
		},
		[onTabChange],
	);

	const contextValues = useMemo(
		() => ({
			tabs,
			tabsActions,
			activeTab,
			setActiveTab,
			handleTabChange,
		}),
		[tabs, activeTab, tabsActions, handleTabChange],
	);

	useEffect(() => {
		if (!activeTab && tabs.size > 0) {
			setActiveTab(Array.from(tabs.keys())[0]);
		}
	}, [tabs, activeTab]);

	useEffect(() => {
		if (defaultActiveKey && tabs.has(defaultActiveKey) && !defaultSet.current) {
			setActiveTab(defaultActiveKey);
			defaultSet.current = true;
		}
	}, [tabs, defaultActiveKey]);

	return (
		<TabsContext.Provider value={contextValues}>
			{children}
		</TabsContext.Provider>
	);
};

const useTabsContext = () => {
	const context = useContext(TabsContext);
	if (!context) {
		throw new Error("useTabsContext must be used within a TabsProvider");
	}
	return context;
};

interface TabNavProps extends HTMLAttributes<HTMLDivElement> {
	actions?: ReactNode;
}

const TabNavigation: FC<Readonly<TabNavProps>> = ({ actions, ...rest }) => {
	const { activeTab, tabs, handleTabChange } = useTabsContext();

	const tabsArray = useMemo(() => Array.from(tabs), [tabs]);

	return (
		<nav {...rest}>
			<ul>
				{tabsArray.map((tab) => (
					<li key={`tab-btn-${tab}`}>
						<Button
							variant="ghost"
							size="sm"
							className={activeTab === tab[0] ? "active" : ""}
							onClick={() => handleTabChange(tab[0])}
						>
							{tab[1].title ?? tab[1].key}
						</Button>
					</li>
				))}
				{actions ? (
					<li className="ntabs-actions" key="tab-actions">
						{actions}
					</li>
				) : null}
			</ul>
		</nav>
	);
};

interface Props extends PropsWithChildren, HtmlHTMLAttributes<HTMLDivElement> {
	defaultActiveKey?: string;
	onTabChange?: (eventKey: string) => void;
	/** Extra controls rendered on the right end of the tab row. */
	actions?: ReactNode;
}

const Tabs: FC<Readonly<Props>> = ({
	children,
	defaultActiveKey,
	className,
	onTabChange,
	actions,
	...rest
}) => {
	return (
		<div className={`ntabs${className ? ` ${className}` : ""}`} {...rest}>
			{/* TODO: change class when bootstrap tabs are deprecated */}
			<TabsProvider
				defaultActiveKey={defaultActiveKey}
				onTabChange={onTabChange}
			>
				<TabNavigation actions={actions} />
				{children}
			</TabsProvider>
		</div>
	);
};

interface TabPanelProps
	extends PropsWithChildren,
		Omit<HTMLAttributes<HTMLDivElement>, "title"> {
	eventKey: string;
	title?: ReactNode;
	disabled?: boolean;
}

const TabPanel: FC<Readonly<TabPanelProps>> = ({
	eventKey,
	title,
	disabled,
	children,
	className,
	...rest
}) => {
	const { activeTab, tabsActions } = useTabsContext();
	const mountedRef = useRef(false);

	const isActive = eventKey === activeTab;

	const tabSettings = useMemo(
		() => ({
			key: eventKey,
			title: title ?? (eventKey as ReactNode),
			disabled,
		}),
		[eventKey, title, disabled],
	);

	useEffect(() => {
		if (!mountedRef.current) {
			tabsActions.set(eventKey, tabSettings);

			mountedRef.current = true;
		}
	}, [eventKey, tabSettings, tabsActions]);

	if (!isActive) {
		return null;
	}

	return (
		<Suspense fallback={<ContentLoading />}>
			<GenericErrorBoundary>
				<div
					role="tabpanel"
					{...rest}
					className={`panel${isActive ? " active" : ""}${className ? ` ${className}` : ""}`}
				>
					{children}
				</div>
			</GenericErrorBoundary>
		</Suspense>
	);
};

const TabsNamespace = Object.assign(Tabs, {
	Panel: TabPanel,
});

export { TabsNamespace as Tabs };
